import BASE_URL from './api';
// Utilitário de autenticação - usar em qualquer componente// Evento customizado para notificar sessão expirada
export const SESSION_EXPIRED_EVENT = 'sessionExpired';

// Disparar evento de sessão expirada (usado pelo App.js para mostrar popup)
export function dispatchSessionExpired() {
    window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
}

// Obter usuário logado
export function getUsuarioLogado() {
    const userInfo = localStorage.getItem('userInfo');
    if (userInfo) {
        return JSON.parse(userInfo);
    }
    return null;
}

// Obter ID do usuário logado
export function getUsuarioSistemaId() {
    const id = localStorage.getItem('usuarioSistemaId');
    return id ? parseInt(id, 10) : null;
}

// Obter token de autenticação
export function getAuthToken() {
    return localStorage.getItem('authToken');
}

// Decodificar payload do JWT (sem verificar assinatura)
function decodeJwtPayload(token) {
    try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
            atob(base64)
                .split('')
                .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join('')
        );
        return JSON.parse(jsonPayload);
    } catch {
        return null;
    }
}

// CPF/CNPJ da empresa logada (claim TenantCpfCnpj do login) — vai nos links públicos, como o de aprovação do orçamento
export function getTenantCpfCnpj() {
    const token = getAuthToken();
    const payload = token ? decodeJwtPayload(token) : null;
    return payload?.TenantCpfCnpj || payload?.tenantCpfCnpj || '';
}

// Verificar se o token JWT está expirado
export function isTokenExpired() {
    const token = getAuthToken();
    if (!token) {
        console.warn('[AUTH] isTokenExpired: nenhum token encontrado no localStorage');
        return true;
    }
    
    const payload = decodeJwtPayload(token);
    if (!payload || !payload.exp) {
        console.warn('[AUTH] isTokenExpired: payload inválido ou sem exp', { payload, tokenStart: token.substring(0, 30) });
        return true;
    }
    
    // exp é em segundos, Date.now() em milissegundos
    const expirationTime = payload.exp * 1000;
    const now = Date.now();
    const expired = now >= expirationTime;
    console.log(`[AUTH] isTokenExpired: now=${now}, exp=${expirationTime}, diff=${(expirationTime - now) / 1000}s, expired=${expired}`);
    return expired;
}

// Verificar se está logado (e se o token não expirou)
export function isLoggedIn() {
    const token = getAuthToken();
    const userInfo = getUsuarioLogado();
    if (!token || !userInfo) {
        console.log('[AUTH] isLoggedIn: false — token:', !!token, 'userInfo:', !!userInfo);
        return false;
    }
    
    // Verificar se o token expirou
    if (isTokenExpired()) {
        console.log('[AUTH] isLoggedIn: false — token expirado');
        return false;
    }
    
    return true;
}

// Fazer logout
export function logout() {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userInfo');
    localStorage.removeItem('usuarioSistemaId');
    localStorage.removeItem('lastActivity');
}

// Registrar atividade do usuário (para monitorar inatividade)
export function registerActivity() {
    localStorage.setItem('lastActivity', Date.now().toString());
}

// Verificar inatividade (1 hora = 3600000 ms)
export const INACTIVITY_LIMIT_MS = 60 * 60 * 1000; // 1 hora

export function checkInactivity() {
    const lastActivity = localStorage.getItem('lastActivity');
    if (!lastActivity) return false;
    
    const elapsed = Date.now() - parseInt(lastActivity, 10);
    return elapsed >= INACTIVITY_LIMIT_MS;
}

// Fazer chamada autenticada à API
export async function apiCallAuth(endpoint, method = 'GET', data = null) {
    const token = getAuthToken();
    const url = `${BASE_URL}/${endpoint}`;
    
    if (!token) {
        dispatchSessionExpired();
        throw new Error('Você precisa estar logado para realizar esta ação. Faça login novamente.');
    }
    
    // Verificar se token expirou antes de fazer a chamada
    if (isTokenExpired()) {
        logout();
        dispatchSessionExpired();
        throw new Error('Sessão expirada. Faça login novamente.');
    }
    
    const config = {
        method,
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
    };
    
    if (data && method !== 'GET') {
        config.body = JSON.stringify(data);
    }

    const response = await fetch(url, config);
    
    if (response.status === 401) {
        // Token inválido ou expirado - fazer logout e disparar evento
        logout();
        dispatchSessionExpired();
        throw new Error('Sessão expirada. Faça login novamente.');
    }
    
    if (!response.ok) {
        const text = await response.text();
        throw new Error(text || `Erro ${response.status}`);
    }
    
    // Registrar atividade após chamada bem-sucedida
    registerActivity();
    
    return await lerRespostaJson(response);
}

// PUT/DELETE costumam responder sem corpo (204): devolve null em vez de quebrar no JSON
async function lerRespostaJson(response) {
    const text = await response.text();
    if (!text) return null;
    try {
        return JSON.parse(text);
    } catch {
        return text;
    }
}

// Fazer chamada à API (sem autenticação obrigatória, mas envia token se disponível)
export async function apiCall(endpoint, method = 'GET', data = null) {
    const url = `${BASE_URL}/${endpoint}`;
    const token = getAuthToken();
    
    const headers = {
        'Content-Type': 'application/json',
    };
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }
    
    const config = {
        method,
        headers,
    };
    
    if (data && method !== 'GET') {
        config.body = JSON.stringify(data);
    }

    const response = await fetch(url, config);
    
    if (!response.ok) {
        const text = await response.text();
        throw new Error(text || `Erro ${response.status}`);
    }
    
    return await lerRespostaJson(response);
}

export default {
    getUsuarioLogado,
    getUsuarioSistemaId,
    getAuthToken,
    isLoggedIn,
    logout,
    apiCall,
    apiCallAuth,
    BASE_URL
};
